import { Fragment, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { PAYMENT_METHODS, type InvoiceDocumentKind, type InvoiceLine, type InvoiceLineSection, type ShopTypeLabels } from "@autorepair/contracts";
import { customersApi, invoicesApi, paymentsApi, shopProfileApi, workOrdersApi, ApiError } from "../lib/api";
import { formatDate, formatDateTime, formatInvoiceNumber, formatInvoiceTaxRateLabel, formatMoney, formatQty, formatWorkOrderNumber, parseMoneyInput } from "../lib/money";
import { toIsoDate } from "../lib/dateRange";
import { useConfirmGate } from "../lib/useConfirm";
import { EmailDocumentDialog } from "../components/EmailDocumentDialog";
import { EmailSetupHint } from "../components/EmailSetupHint";
import { useShopTypeLabels } from "../lib/theme";
import { Spinner } from "../components/ui-modern";
import {
  Eyebrow,
  ModernBreadcrumbs,
  DownloadIcon,
  EmailIcon,
  ModernButton,
  PrintIcon,
  ModernCard,
  ModernConfirmButton,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernModal,
  ModernSelect,
  ModernStatusPill,
} from "../components/ui-modern";

// Same section grouping helper as Classic — private duplicate, same reason.
// Live, not frozen — matches the PDF's own treatment (buildInvoiceViewModel):
// a re-view always shows the shop's CURRENT shopType labels, not whatever
// was in effect at issue time (see docs/SHOP_TYPES_PLAN.md).
function sectionLabels(labels: ShopTypeLabels): Record<InvoiceLineSection, string> {
  return { parts: labels.partLabel, labor: labels.laborLabel, sublet: labels.subletLabel };
}
const SECTION_ORDER: InvoiceLineSection[] = ["parts", "labor", "sublet"];
// A zero-amount Shop Supplies row (removed/overridden to "0.00") never
// shows here, same as the printed PDF (see buildInvoiceViewModel in
// apps/api/src/pdf/invoice-view-model.ts) — it's just visual noise
// otherwise, not a line the shop actually charged for.
function groupLinesBySection(lines: InvoiceLine[]): [InvoiceLineSection, InvoiceLine[]][] {
  const visibleLines = lines.filter((l) => l.lineKind !== "shop_supplies" || l.extendedAmount !== "0.00");
  // Shop Supplies always sorts to the END of the parts group, matching the PDF.
  const isSupplies = (l: InvoiceLine) => l.lineKind === "shop_supplies";
  return SECTION_ORDER.map((section) => {
    const group = visibleLines.filter((l) => l.section === section);
    return [section, [...group.filter((l) => !isSupplies(l)), ...group.filter(isSupplies)]] as [
      InvoiceLineSection,
      InvoiceLine[],
    ];
  }).filter(([, group]) => group.length > 0);
}

/**
 * The modern theme's Invoice Detail screen — see docs/FRONTEND_BACKLOG.md
 * Round 59/60 and `Bay2Bill Redesign.dc.html` lines ~440-537 ("isInvoice").
 *
 * All data-fetching and mutations below are a deliberate duplicate of
 * InvoiceDetailPageClassic.tsx's own (same queryKeys/queryFns/mutationFns,
 * same invalidation calls) rather than a shared hook, same tradeoff every
 * other Modern screen this round has made. This page in particular was the
 * subject of THREE separate cache-key bugs (see Round 58) — every
 * invalidation below is copied verbatim from the already-fixed Classic
 * file, not reconstructed from first principles.
 *
 * Layout differs from the mockup's literal static demo in one deliberate
 * way, documented here rather than silently: the mockup's big left card is
 * a HAND-DRAWN recreation of what an invoice looks like, built from fake
 * demo data. This app instead shows the same identity/totals/lifecycle
 * summary Classic's own "Summary" card already shows (real fields, not
 * fabricated). There is no standing preview `<iframe>` on this page (see
 * docs/FRONTEND_BACKLOG.md's Send & Print round) — "Print customer/shop
 * copy" loads the PDF into a hidden iframe only at click time and calls
 * `contentWindow.print()` once it's ready, and "Download" is a plain link;
 * neither triggers a PDF render just from viewing the page.
 */
export function InvoiceDetailPageModern() {
  const SECTION_LABELS = sectionLabels(useShopTypeLabels());
  const { invoiceNumber: invoiceNumberParam } = useParams();
  const invoiceNumber = Number(invoiceNumberParam);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { from?: string } | null)?.from ?? "/invoices";
  const cameFromWorkOrder = returnTo.startsWith("/work-orders/");
  const queryClient = useQueryClient();
  // Backs BOTH print buttons — a single hidden iframe, pointed at whichever
  // copy was last requested and print()'d once it finishes loading. Not
  // mounted with a src until a Print button is clicked, so viewing this
  // page never triggers a PDF render the shop didn't ask for. `token` is a
  // cache-buster: printing the SAME copy twice in a row must still change
  // the src (otherwise the iframe never re-fires onLoad, and print() never
  // gets called the second time).
  const printFrameRef = useRef<HTMLIFrameElement>(null);
  const [printRequest, setPrintRequest] = useState<{ kind: InvoiceDocumentKind; token: number } | null>(null);
  const [printing, setPrinting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [archiveChoiceOpen, setArchiveChoiceOpen] = useState(false);
  const paymentConfirm = useConfirmGate();
  const deleteConfirm = useConfirmGate();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const invoiceQuery = useQuery({
    queryKey: ["invoice", "by-number", invoiceNumber],
    queryFn: () => invoicesApi.getByNumber(invoiceNumber),
  });
  const id = invoiceQuery.data?.invoice.id as number;
  const workOrderIdForCrumb = invoiceQuery.data?.invoice.workOrderId;
  const workOrderQuery = useQuery({
    queryKey: ["work-order", workOrderIdForCrumb],
    queryFn: () => workOrdersApi.get(workOrderIdForCrumb!),
    enabled: workOrderIdForCrumb != null,
  });

  // See InvoiceDetailPageClassic.tsx's own comment on this exact block —
  // copied verbatim: the invoice itself is keyed ["invoice", "by-number",
  // invoiceNumber], NOT ["invoice", id], so every mutation below must
  // invalidate that same key (Round 58's bug class).
  const voidMutation = useMutation({
    mutationFn: (reason: string) => invoicesApi.void(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invoice", "by-number", invoiceNumber] });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      // Voiding the last active invoice moves its work order back to 'complete' (editable again).
      void queryClient.invalidateQueries({ queryKey: ["work-order"] });
      void queryClient.invalidateQueries({ queryKey: ["work-orders"] });
    },
    onError: (err) => setNotice(err instanceof ApiError ? err.message : "Could not void this invoice."),
  });

  const archiveMutation = useMutation({
    mutationFn: () => invoicesApi.archive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invoice", "by-number", invoiceNumber] });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (err) => setNotice(err instanceof ApiError ? err.message : "Could not archive this invoice."),
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => invoicesApi.unarchive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invoice", "by-number", invoiceNumber] });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (err) => setNotice(err instanceof ApiError ? err.message : "Could not restore this invoice."),
  });

  const deleteMutation = useMutation({
    mutationFn: () => invoicesApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      const workOrderId = invoiceQuery.data?.invoice.workOrderId;
      if (workOrderId != null) void queryClient.invalidateQueries({ queryKey: ["work-order", workOrderId] });
      const workOrderNumber = workOrderQuery.data?.aggregate.workOrder.workOrderNumber;
      if (workOrderNumber != null) {
        void queryClient.invalidateQueries({ queryKey: ["work-order", "by-number", workOrderNumber] });
      }
      void queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      navigate(returnTo);
    },
    onError: (err) => setNotice(err instanceof ApiError ? err.message : "Could not delete this invoice."),
  });

  function requestPrint(kind: InvoiceDocumentKind): void {
    setPrinting(true);
    setPrintRequest({ kind, token: Date.now() });
  }

  // null = untouched, so the field shows the balance due until the user edits
  // it (an empty string must stay empty, or backspace can't clear it).
  const [amount, setAmount] = useState<string | null>(null);

  // Emailing the invoice needs BOTH a customer email and a configured shop
  // email (the shop's is the Reply-To) — the button hides otherwise.
  const shopProfileQuery = useQuery({ queryKey: ["shop-profile"], queryFn: shopProfileApi.get });
  const emailCustomerId = invoiceQuery.data?.invoice.customerId;
  const emailCustomerQuery = useQuery({
    queryKey: ["customer", emailCustomerId],
    queryFn: () => customersApi.get(emailCustomerId!),
    enabled: !!emailCustomerId,
  });
  const customerEmail = emailCustomerQuery.data?.customer.email?.trim() || null;
  const canEmailInvoice = !!customerEmail && !!shopProfileQuery.data?.shopProfile.email;
  const invoiceEmailsQuery = useQuery({
    queryKey: ["document-emails", "invoice", id],
    queryFn: () => invoicesApi.emailSummary(id),
    enabled: !!invoiceQuery.data,
  });
  const [method, setMethod] = useState<(typeof PAYMENT_METHODS)[number]>("credit_card");
  // Local calendar date, not toISOString() (UTC): an evening payment in Texas would otherwise be
  // dated tomorrow, which also skews the Payments page's day-by-day tender totals.
  const [receivedOn, setReceivedOn] = useState(() => toIsoDate(new Date()));
  const [reference, setReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const amountValue = amount ?? invoiceQuery.data?.invoice.balanceDue ?? "";
  const paymentMutation = useMutation({
    mutationFn: () => {
      const parsed = parseMoneyInput(amountValue);
      if (!parsed) throw new Error('Amount should look like "100.00".');
      return paymentsApi.record(id, {
        amount: parsed,
        method,
        receivedOn,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(paymentNotes.trim() ? { notes: paymentNotes.trim() } : {}),
      });
    },
    onSuccess: async () => {
      setAmount(null);
      setReference("");
      setPaymentNotes("");
      await queryClient.invalidateQueries({ queryKey: ["invoice", "by-number", invoiceNumber] });
    },
    onError: (err) => setNotice(err instanceof ApiError || err instanceof Error ? err.message : "Could not record this payment."),
  });

  const voidPaymentMutation = useMutation({
    mutationFn: (paymentId: number) => paymentsApi.void(paymentId, "Voided from the invoice screen."),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoice", "by-number", invoiceNumber] });
    },
    onError: (err) => setNotice(err instanceof ApiError ? err.message : "Could not void this payment."),
  });

  const deletePaymentMutation = useMutation({
    mutationFn: (paymentId: number) => paymentsApi.delete(paymentId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["invoice", "by-number", invoiceNumber] }),
    onError: (err) => setNotice(err instanceof ApiError ? err.message : "Could not delete this payment record."),
  });

  const unvoidPaymentMutation = useMutation({
    mutationFn: (paymentId: number) => paymentsApi.unvoid(paymentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invoice", "by-number", invoiceNumber] });
    },
    onError: (err) => setNotice(err instanceof ApiError ? err.message : "Could not un-void this payment."),
  });

  if (invoiceQuery.isLoading) return <Spinner label="Loading invoice…" />;
  if (invoiceQuery.error || !invoiceQuery.data) return <ModernErrorBanner message="Could not find that invoice." />;

  const { invoice, lines, payments } = invoiceQuery.data;
  const showPaymentForm = invoice.status !== "void" && invoice.balanceDue !== "0.00";
  // See money.ts's own comment on formatTaxRateLabel's hasTaxableLine
  // param — a category with only individually-tax-exempt lines (or no
  // line at all) shouldn't show its rate next to Tax.
  const taxRateLabel = formatInvoiceTaxRateLabel(invoice, SECTION_LABELS, {
    parts: lines.some((l) => l.section === "parts" && l.isTaxable),
    labor: lines.some((l) => l.section === "labor" && l.isTaxable),
    sublet: lines.some((l) => l.section === "sublet" && l.isTaxable),
  });
  const lineSections = groupLinesBySection(lines);

  const breadcrumbItems = cameFromWorkOrder
    ? [
        { label: "Customers", to: "/customers" },
        { label: invoice.customerNameSnapshot, to: `/customers/${invoice.customerNumberSnapshot}` },
        ...(workOrderQuery.data
          ? [
              {
                label: `Work Order ${formatWorkOrderNumber(workOrderQuery.data.aggregate.workOrder.workOrderNumber)}`,
                to: `/work-orders/${workOrderQuery.data.aggregate.workOrder.workOrderNumber}`,
              },
            ]
          : []),
        { label: `Invoice ${formatInvoiceNumber(invoice.invoiceNumber)}` },
      ]
    : [{ label: "Invoices", to: "/invoices" }, { label: `Invoice ${formatInvoiceNumber(invoice.invoiceNumber)}` }];

  const odometerLine =
    invoice.odometerInSnapshot != null || invoice.odometerOutSnapshot != null
      ? [
          invoice.odometerInSnapshot != null ? `${invoice.odometerInSnapshot.toLocaleString()} mi in` : null,
          invoice.odometerOutSnapshot != null ? `${invoice.odometerOutSnapshot.toLocaleString()} mi out` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;
  // "—" is buildVehicleDescription()'s sentinel for a fully-empty vehicle
  // (see apps/api/src/common/pdf-identity.ts) — the auto-provisioned
  // per-customer placeholder for a `detailing` tenant is always exactly
  // that, so this is how the page knows there's no real vehicle to show.
  const hasRealVehicle = invoice.vehicleDescriptionSnapshot !== "—";

  return (
    <div>
      <ModernBreadcrumbs items={breadcrumbItems} />
      {notice && <ModernErrorBanner message={notice} />}
      {invoice.archivedAt && (
        <div className="mb-4 rounded-[10px] border border-[#e2e7ee] bg-[#f6f8fb] px-4 py-3 text-[13.5px] text-[#4a5361]">
          This invoice is archived (hidden from the main list). It is still a permanent record.
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1.65fr)_minmax(280px,.85fr)]">
        {/* LEFT: identity + totals + lifecycle, matching the mockup's own
            header/identity/totals blocks with real snapshot fields — see
            this file's header comment for why the mockup's own hand-drawn
            line-item recreation isn't duplicated here. */}
        <ModernCard className="min-w-0 p-[26px]">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#eef1f6] pb-5">
            <div>
              <div className="text-[17px] font-bold text-[#071a33]">{invoice.shopNameSnapshot}</div>
              {(invoice.shopAddressSnapshot || invoice.shopPhoneSnapshot) && (
                <div className="mt-1 text-[12.5px] leading-relaxed text-[#6b7a8f]">
                  {invoice.shopAddressSnapshot}
                  {invoice.shopAddressSnapshot && invoice.shopPhoneSnapshot && <br />}
                  {invoice.shopPhoneSnapshot}
                </div>
              )}
              {invoice.shopTaxRegistrationNumberSnapshot && (
                <div className="mt-1 text-[12.5px] leading-relaxed text-[#6b7a8f]">
                  GST/HST# {invoice.shopTaxRegistrationNumberSnapshot}
                </div>
              )}
            </div>
            <div className="text-right">
              <Eyebrow className="text-[11px] text-[#0f6d86]">Invoice</Eyebrow>
              <div className="mt-1.5 font-mono text-[19px] font-semibold text-[#071a33]" style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}>
                {formatInvoiceNumber(invoice.invoiceNumber)}
              </div>
              <div className="mt-1.5 text-[12.5px] text-[#6b7a8f]">Issued {formatDate(invoice.issuedOn)}</div>
              <div className="mt-2">
                <ModernStatusPill status={invoice.status} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 border-b border-[#eef1f6] py-5 sm:grid-cols-3">
            <div>
              <Eyebrow className="text-[12.5px] text-[#4a5361]" weight={700}>
                Billed to
              </Eyebrow>
              <Link to={`/customers/${invoice.customerNumberSnapshot}`} className="mt-1.5 block text-[14px] font-semibold text-[#071a33] no-underline hover:underline">
                {invoice.customerNameSnapshot}
              </Link>
              {invoice.customerPhoneSnapshot && (
                <div className="mt-1 text-[12.5px] leading-relaxed text-[#6b7a8f]">{invoice.customerPhoneSnapshot}</div>
              )}
            </div>
            <div>
              {hasRealVehicle ? (
                <>
                  <Eyebrow className="text-[12.5px] text-[#4a5361]" weight={700}>
                    Vehicle
                  </Eyebrow>
                  <div className="mt-1.5 text-[14px] font-semibold text-[#071a33]">{invoice.vehicleDescriptionSnapshot}</div>
                  {(invoice.vehiclePlateSnapshot || invoice.vehicleVinSnapshot || odometerLine) && (
                    <div className="mt-1 text-[12.5px] leading-relaxed text-[#6b7a8f]">
                      {invoice.vehiclePlateSnapshot && <>Plate {invoice.vehiclePlateSnapshot}</>}
                      {invoice.vehiclePlateSnapshot && invoice.vehicleVinSnapshot && <br />}
                      {invoice.vehicleVinSnapshot && <>VIN {invoice.vehicleVinSnapshot}</>}
                      {(invoice.vehiclePlateSnapshot || invoice.vehicleVinSnapshot) && odometerLine && <br />}
                      {odometerLine}
                    </div>
                  )}
                  {invoice.flexField1LabelSnapshot && invoice.flexField1Snapshot && (
                    <div className="mt-1 text-[12.5px] leading-relaxed text-[#6b7a8f]">
                      {invoice.flexField1LabelSnapshot} {invoice.flexField1Snapshot}
                    </div>
                  )}
                </>
              ) : invoice.flexField1LabelSnapshot && invoice.flexField1Snapshot ? (
                <>
                  <Eyebrow className="text-[12.5px] text-[#4a5361]" weight={700}>
                    {invoice.flexField1LabelSnapshot}
                  </Eyebrow>
                  <div className="mt-1.5 text-[14px] font-semibold text-[#071a33]">{invoice.flexField1Snapshot}</div>
                </>
              ) : null}
            </div>
            <div>
              <Eyebrow className="text-[12.5px] text-[#4a5361]" weight={700}>
                Work Order
              </Eyebrow>
              {workOrderQuery.data ? (
                <Link
                  to={`/work-orders/${workOrderQuery.data.aggregate.workOrder.workOrderNumber}`}
                  className="mt-1.5 block text-[14px] font-semibold text-[#071a33] no-underline hover:underline"
                >
                  {formatWorkOrderNumber(workOrderQuery.data.aggregate.workOrder.workOrderNumber)}
                </Link>
              ) : (
                <div className="mt-1.5 text-[14px] font-semibold text-[#071a33]">…</div>
              )}
              {(invoice.customerConcernSnapshot || invoice.techniciansSnapshot) && (
                <div className="mt-1 text-[12.5px] leading-relaxed text-[#6b7a8f]">
                  {invoice.customerConcernSnapshot}
                  {invoice.customerConcernSnapshot && invoice.techniciansSnapshot && <br />}
                  {invoice.techniciansSnapshot && <>Tech: {invoice.techniciansSnapshot}</>}
                </div>
              )}
            </div>
          </div>

          {/* One table, exactly the work-order editor's own Parts/Labor/
              Sublet shape (WorkOrderEditorPageModern.tsx): the FIRST
              section's own name doubles as the column-header row (Qty/
              Price/Total on that same line, no separate "Description"
              heading) — later sections just get a plain name bar, since
              the columns are already established. Read-only: no actions
              column, and no ModernInfoTip on Sublet (that tip explains
              entry-time behavior that doesn't apply to an already-issued,
              frozen line). */}
          <div className="overflow-x-auto" style={{ marginTop: 6 }}>
            <table className="w-full min-w-[480px] border-collapse text-[14px]">
              <tbody>
                {lineSections.map(([section, group], sectionIndex) => (
                  <Fragment key={section}>
                    <tr className={sectionIndex === 0 ? "bg-[#f8fafc]" : undefined}>
                      <td
                        colSpan={sectionIndex === 0 ? undefined : 4}
                        className="border-b border-[#eef1f6] bg-[#f8fafc] pb-2 pt-3 font-mono text-[13px] font-bold uppercase tracking-[.08em] text-[#071a33]"
                        style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                      >
                        {SECTION_LABELS[section]}
                      </td>
                      {sectionIndex === 0 && (
                        <>
                          <td className="border-b border-[#eef1f6] px-2 py-2 text-right font-mono text-[11px] font-semibold uppercase tracking-[.1em] text-[#6b7a8f]">
                            Qty
                          </td>
                          <td className="border-b border-[#eef1f6] px-2 py-2 text-right font-mono text-[11px] font-semibold uppercase tracking-[.1em] text-[#6b7a8f]">
                            Price
                          </td>
                          <td className="border-b border-[#eef1f6] py-2 text-right font-mono text-[11px] font-semibold uppercase tracking-[.1em] text-[#6b7a8f]">
                            Total
                          </td>
                        </>
                      )}
                    </tr>
                    {group.map((l) => (
                      <tr key={l.id}>
                        <td className="border-b border-[#f1f4f9] py-2.5">
                          {l.partNumber && !l.isStateInspection && (
                            <div
                              className="font-mono text-[13px] font-semibold text-[#101b2d]"
                              style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
                            >
                              {l.partNumber}
                            </div>
                          )}
                          <div className="text-[#101b2d]">{l.description}</div>
                          {l.isStateInspection && (
                            <div className="text-[12px] text-[#0f6d86]">
                              Tax-exempt state fee{l.inspectionDetail ? ` · ${l.inspectionDetail}` : ""}
                            </div>
                          )}
                        </td>
                        <td className="border-b border-[#f1f4f9] px-2 py-2.5 text-right tabular-nums text-[#4a5361]">
                          {l.quantity ? formatQty(l.quantity) : ""}
                        </td>
                        <td className="border-b border-[#f1f4f9] px-2 py-2.5 text-right tabular-nums text-[#4a5361]">
                          {l.unitPrice ? formatMoney(l.unitPrice) : ""}
                        </td>
                        <td className="whitespace-nowrap border-b border-[#f1f4f9] py-2.5 text-right font-semibold tabular-nums text-[#071a33]">
                          {formatMoney(l.extendedAmount)}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex justify-end">
            <dl className="m-0 grid w-full max-w-[280px] grid-cols-[1fr_auto] gap-y-2 text-[13.5px]">
              <dt className="text-[#6b7a8f]">Subtotal</dt>
              <dd className="m-0 text-right tabular-nums">{formatMoney(invoice.subtotal)}</dd>
              {invoice.discountTotal !== "0.00" && (
                <>
                  <dt className="text-[#6b7a8f]">
                    Discount{invoice.discountPercentSnapshot != null ? ` (${Number(invoice.discountPercentSnapshot)}%)` : ""}
                  </dt>
                  <dd className="m-0 text-right tabular-nums">−{formatMoney(invoice.discountTotal)}</dd>
                </>
              )}
              <dt className="text-[#6b7a8f]">
                {invoice.shopTaxLabelSnapshot}
                {taxRateLabel ? ` ${taxRateLabel}` : ""}
              </dt>
              <dd className="m-0 text-right tabular-nums">{formatMoney(invoice.taxTotal)}</dd>
              <dt className="text-[#6b7a8f]">Paid</dt>
              <dd className="m-0 text-right tabular-nums text-[#1a6b2c]">{formatMoney(invoice.amountPaid)}</dd>
              <dt className="border-t-2 border-[#071a33] pt-2.5 text-[16px] font-bold text-[#071a33]">Balance due</dt>
              <dd
                className={`m-0 border-t-2 border-[#071a33] pt-2.5 text-right text-[16px] font-bold tabular-nums ${
                  invoice.balanceDue !== "0.00" ? "text-[#b3261e]" : "text-[#071a33]"
                }`}
              >
                {formatMoney(invoice.balanceDue)}
              </dd>
            </dl>
          </div>

          {invoice.termsTextSnapshot && (
            <p className="mt-5 border-t border-[#eef1f6] pt-4 text-[12px] leading-relaxed text-[#8b97a8]">{invoice.termsTextSnapshot}</p>
          )}
        </ModernCard>

        {/* RIGHT rail */}
        <aside className="flex min-w-0 flex-col gap-3.5 lg:sticky lg:top-4">
          {showPaymentForm && (
            <ModernCard>
              <h2 className="m-0 mb-3.5 text-[14px] font-bold text-[#071a33]">Record a payment</h2>
              <form
                className="flex flex-col gap-2.5"
                onSubmit={(e) => {
                  e.preventDefault();
                  paymentConfirm.request(() => paymentMutation.mutate());
                }}
              >
                <ModernField label="Amount" htmlFor="m-payment-amount" required>
                  <ModernInput
                    id="m-payment-amount"
                    decimal
                    required
                    placeholder="0.00"
                    value={amountValue}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </ModernField>
                <ModernField label="Method" htmlFor="m-payment-method">
                  <ModernSelect id="m-payment-method" value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m.replace("_", " ")}
                      </option>
                    ))}
                  </ModernSelect>
                </ModernField>
                <ModernField label="Date received" htmlFor="m-payment-date" required>
                  <ModernInput id="m-payment-date" type="date" required value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
                </ModernField>
                <ModernField label={method === "check" ? "Check #" : "Reference / auth # (optional)"} htmlFor="m-payment-reference">
                  <ModernInput id="m-payment-reference" maxLength={100} value={reference} onChange={(e) => setReference(e.target.value)} />
                  {(method === "credit_card" || method === "debit_card") && (
                    <p className="m-0 mt-1 text-[12px] text-[#6b7a8f]">
                      Cards are charged on your own terminal. Type the auth/transaction # from its receipt so you can match it later.
                    </p>
                  )}
                </ModernField>
                <ModernField label="Note (optional)" htmlFor="m-payment-notes">
                  <ModernInput id="m-payment-notes" maxLength={500} value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} />
                </ModernField>
                <ModernButton type="submit" className="w-full" disabled={paymentMutation.isPending}>
                  Record payment
                </ModernButton>
              </form>
            </ModernCard>
          )}

          <ModernCard>
            <h2 className="m-0 mb-2.5 text-[14px] font-bold text-[#071a33]">Payment history</h2>
            {payments.length === 0 && <p className="m-0 text-[13px] text-[#8b97a8]">No payments recorded yet.</p>}
            <div className="flex flex-col gap-2">
              {payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 border-b border-[#f1f4f9] py-2.5 text-[13px] last:border-b-0">
                  <span className={`min-w-0 ${p.voidedAt ? "text-[#8b97a8] line-through" : "text-[#101b2d]"}`}>
                    {formatDate(p.receivedOn)} · {p.method.replace("_", " ")}
                    {p.reference && <span className="block truncate text-[12px] text-[#6b7a8f]">Ref {p.reference}</span>}
                    {p.notes && <span className="block truncate text-[12px] text-[#6b7a8f]">{p.notes}</span>}
                  </span>
                  <span className="flex flex-none items-center gap-2.5">
                    <span className="font-semibold tabular-nums text-[#071a33]">{formatMoney(p.amount)}</span>
                    {!p.voidedAt && (
                      <button type="button" className="text-[12px] font-semibold text-[#b3261e] underline" onClick={() => voidPaymentMutation.mutate(p.id)}>
                        Void
                      </button>
                    )}
                    {p.voidedAt && (
                      <>
                        <button
                          type="button"
                          className="text-[12px] font-semibold text-[#0f6d86] underline"
                          onClick={() => unvoidPaymentMutation.mutate(p.id)}
                        >
                          Un-void
                        </button>
                        <button
                          type="button"
                          className="text-[12px] font-semibold text-[#b3261e] underline"
                          onClick={() => deletePaymentMutation.mutate(p.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </ModernCard>

          <ModernCard>
            <h2 className="m-0 mb-3 text-[14px] font-bold text-[#071a33]">Send &amp; print</h2>
            {/* Hidden — not a preview, just the document a Print button
                loads and calls contentWindow.print() on once ready. No src
                until a Print button is clicked, so viewing this page never
                triggers a PDF render the shop didn't ask for. */}
            <iframe
              ref={printFrameRef}
              title="Invoice print"
              src={printRequest ? `${invoicesApi.documentUrl(id, printRequest.kind)}?t=${printRequest.token}` : undefined}
              onLoad={() => {
                if (printRequest) printFrameRef.current?.contentWindow?.print();
                setPrinting(false);
              }}
              style={{ position: "absolute", width: 0, height: 0, border: 0, left: "-9999px" }}
            />
            <div className="flex flex-col gap-1.5">
              <div className="flex gap-1.5">
                <ModernButton
                  type="button"
                  variant="secondary"
                  className="min-w-0 flex-1 justify-start"
                  disabled={printing}
                  onClick={() => requestPrint("customer")}
                >
                  <PrintIcon />
                  Print Customer Copy
                </ModernButton>
                <a
                  href={invoicesApi.documentUrl(id, "customer", true)}
                  title="Download Customer Copy"
                  aria-label="Download Customer Copy"
                  className="shrink-0"
                >
                  <ModernButton type="button" variant="secondary" className="justify-center !px-3">
                    <DownloadIcon />
                  </ModernButton>
                </a>
              </div>
              <div className="flex gap-1.5">
                <ModernButton
                  type="button"
                  variant="secondary"
                  className="min-w-0 flex-1 justify-start"
                  disabled={printing}
                  onClick={() => requestPrint("shop")}
                >
                  <PrintIcon />
                  Print Shop Copy
                </ModernButton>
                <a
                  href={invoicesApi.documentUrl(id, "shop", true)}
                  title="Download Shop Copy"
                  aria-label="Download Shop Copy"
                  className="shrink-0"
                >
                  <ModernButton type="button" variant="secondary" className="justify-center !px-3">
                    <DownloadIcon />
                  </ModernButton>
                </a>
              </div>
              {invoice.status !== "void" && canEmailInvoice && (
                <ModernButton
                  type="button"
                  variant="secondary"
                  className="w-full justify-start"
                  onClick={() => setEmailDialogOpen(true)}
                >
                  <EmailIcon />
                  Email Invoice to Customer
                </ModernButton>
              )}
              {invoice.status !== "void" && !canEmailInvoice && emailCustomerQuery.data && shopProfileQuery.data && (
                <EmailSetupHint
                  kindLabel="invoice"
                  customerMissing={!customerEmail}
                  shopMissing={!shopProfileQuery.data.shopProfile.email}
                  customerNumber={invoice.customerNumberSnapshot}
                />
              )}
              {invoice.status !== "void" && canEmailInvoice && invoiceEmailsQuery.data && invoiceEmailsQuery.data.count > 0 && (
                <p className="m-0 text-[12.5px] text-[#8b97a8]">
                  Emailed {invoiceEmailsQuery.data.count}× · last to {invoiceEmailsQuery.data.lastSentTo} on{" "}
                  {formatDateTime(invoiceEmailsQuery.data.lastSentAt)}
                </p>
              )}
              {invoice.status !== "void" && (
                <ModernConfirmButton
                  variant="danger"
                  className="w-full justify-start"
                  confirmText="Void this invoice?"
                  onConfirm={() => voidMutation.mutate("Voided from the invoice screen.")}
                >
                  Void invoice
                </ModernConfirmButton>
              )}
              {invoice.status === "void" && !invoice.archivedAt && (
                <ModernButton type="button" variant="danger" className="w-full justify-start" onClick={() => setArchiveChoiceOpen(true)}>
                  Archive or delete…
                </ModernButton>
              )}
              {invoice.archivedAt && (
                <>
                  <ModernButton type="button" variant="secondary" className="w-full justify-start" onClick={() => unarchiveMutation.mutate()}>
                    Restore to list
                  </ModernButton>
                  <ModernButton
                    type="button"
                    variant="danger"
                    className="w-full justify-start"
                    onClick={() => deleteConfirm.request(() => deleteMutation.mutate())}
                  >
                    Delete permanently
                  </ModernButton>
                </>
              )}
            </div>
          </ModernCard>
        </aside>
      </div>

      {workOrderQuery.data && (
        <Link
          to={`/work-orders/${workOrderQuery.data.aggregate.workOrder.workOrderNumber}`}
          className="mt-4 inline-block text-[13.5px] text-[#0f6d86] no-underline hover:underline"
        >
          ← Back to work order
        </Link>
      )}

      <ModernConfirmDialog {...paymentConfirm.dialogProps} message="Record this payment?" />
      {emailDialogOpen && (
        <EmailDocumentDialog
          kindLabel="invoice"
          queryKey={["document-emails", "invoice", id]}
          loadPreview={() => invoicesApi.emailPreview(id)}
          send={() => invoicesApi.email(id)}
          onSent={() => void queryClient.invalidateQueries({ queryKey: ["document-emails", "invoice", id] })}
          onClose={() => setEmailDialogOpen(false)}
        />
      )}
      <ModernConfirmDialog
        {...deleteConfirm.dialogProps}
        confirmLabel="Yes, Delete It"
        danger
        message="Permanently delete this invoice? It stays a record internally (the invoice number is never reused), but it will never show up in this app again, even with 'show archived' on."
      />
      {archiveChoiceOpen && (
        <ModernModal title="Archive or Delete This Invoice?" onCancel={() => setArchiveChoiceOpen(false)}>
          <p className="mb-6 text-[14px] text-[#101b2d]">
            Archiving hides it from the main list but keeps it easy to find again later (with "show archived" on). Deleting hides it
            permanently — you can still archive first and delete later if you're not sure.
          </p>
          <div className="flex flex-wrap gap-2.5">
            <ModernButton
              onClick={() => {
                setArchiveChoiceOpen(false);
                archiveMutation.mutate();
              }}
            >
              Archive
            </ModernButton>
            <ModernButton
              variant="danger"
              onClick={() => {
                setArchiveChoiceOpen(false);
                archiveMutation.mutate(undefined, { onSuccess: () => deleteMutation.mutate() });
              }}
            >
              Delete Permanently
            </ModernButton>
            <ModernButton variant="secondary" onClick={() => setArchiveChoiceOpen(false)}>
              Cancel
            </ModernButton>
          </div>
        </ModernModal>
      )}
    </div>
  );
}
